import {
  TooltipContent,
  TooltipProvider,
  Tooltip as TooltipRoot,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import * as React from "react";
import { isMobile } from "react-device-detect";

type TooltipProps = {
  children: React.ReactNode;
  content: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  delayDuration?: number;
  className?: string;
  sideOffset?: number;
  side?: "top" | "right" | "bottom" | "left";
  /**
   * Render the child itself as the Radix trigger instead of wrapping it in a
   * span. The child must then spread unknown props onto a DOM element and
   * forward its ref (native elements do; most of our components don't).
   * Prefer this for interactive children (e.g. <button>) so aria attributes
   * and focus land on the control itself.
   */
  asChild?: boolean;
  /** Extra classes for the wrapping span (ignored when asChild is set). */
  triggerClassName?: string;
  disabled?: boolean;
};

const Tooltip = ({
  children,
  content,
  className,
  sideOffset,
  side = "top",
  asChild = false,
  triggerClassName,
  disabled,
  ...rootProps
}: TooltipProps) => {
  const [isMobileOpen, setIsMobileOpen] = React.useState(rootProps.open);

  const handleMobileClick = () => {
    if (isMobile) {
      setIsMobileOpen(!isMobileOpen);
    }
  };

  if (disabled) return <>{children}</>;

  let trigger: React.ReactNode;
  if (
    asChild &&
    React.isValidElement<React.HTMLAttributes<HTMLElement>>(children)
  ) {
    // Merge existing onClick with handleMobileClick
    trigger = React.cloneElement(children, {
      onClick: (e: React.MouseEvent<HTMLElement>) => {
        children.props.onClick?.(e);
        handleMobileClick();
      },
    });
  } else {
    // Wrapping span guarantees Radix's trigger props (hover/focus handlers,
    // ref) attach to a real DOM node regardless of what the child does with
    // its props. Child onClicks still fire first via bubbling.
    trigger = (
      <span
        className={cn("inline-flex", triggerClassName)}
        onClick={handleMobileClick}
      >
        {children}
      </span>
    );
  }

  return (
    <TooltipProvider>
      <TooltipRoot
        {...rootProps}
        open={isMobile ? isMobileOpen : rootProps.open}
        onOpenChange={isMobile ? setIsMobileOpen : rootProps.onOpenChange}
      >
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          className={className}
          sideOffset={sideOffset}
          side={side}
        >
          {content}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
};

export default Tooltip;
